import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { PageMeta } from "../seo/PageMeta";

function LegalNavbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navLinks = [
    { label: "Accueil", href: "/" },
    { label: "Nos projets", href: "/projet" },
    { label: "Nous contacter", href: "/contact" },
    { label: "À propos", href: "/about" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.nav
      initial={{ opacity: 0, y: -16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 px-4 md:px-0 transition-all duration-500 ${
        scrolled ? "bg-white/80 backdrop-blur-md shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="mx-auto w-full max-w-6xl py-4 md:py-5">
        <div className="flex items-center justify-between md:hidden">
          <a href="/" className="text-sm font-semibold tracking-wide text-[#1a1a2e]">
            LMS
          </a>
          <button
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label="Ouvrir le menu"
            className="rounded-lg p-2 text-[#1a1a2e] hover:bg-[#1a1a2e]/5"
          >
            <span className="block h-0.5 w-6 bg-current mb-1.5" />
            <span className="block h-0.5 w-6 bg-current mb-1.5" />
            <span className="block h-0.5 w-6 bg-current" />
          </button>
        </div>

        <div className="hidden md:flex justify-center gap-10">
          {navLinks.map(({ label, href }) => (
            <motion.a
              key={label}
              href={href}
              className="relative text-sm font-medium text-[#1a1a2e] tracking-wide"
              whileHover="hover"
              animate="rest"
            >
              {label}
              <motion.span
                className="absolute -bottom-0.5 left-0 h-[1.5px] bg-[#1a1a2e] block"
                variants={{
                  rest: { width: "0%" },
                  hover: { width: "100%" },
                }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              />
            </motion.a>
          ))}
        </div>

        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.98 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
              className="mt-3 rounded-2xl border border-[#1a1a2e]/10 bg-white/95 p-3 shadow-md md:hidden"
            >
              <div className="flex flex-col">
                {navLinks.map(({ label, href }) => (
                  <a
                    key={label}
                    href={href}
                    onClick={() => setMenuOpen(false)}
                    className="rounded-lg px-3 py-2 text-sm font-medium text-[#1a1a2e] hover:bg-[#1a1a2e]/5"
                  >
                    {label}
                  </a>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
}

function LegalFooter() {
  return (
    <footer className="bg-[#0C1925] px-6 py-6 text-white/80">
      <div className="mx-auto flex w-full max-w-6xl flex-col items-center justify-between gap-4 text-sm md:flex-row">
        <p className="text-center text-white/50 md:text-left">
          © {new Date().getFullYear()} LMS Technologies. Tous droits réservés.
        </p>
        <div className="flex w-full items-center justify-center gap-5 text-center lg:w-auto lg:justify-start lg:text-left">
          <a href="/condition" className="text-white/60 transition-colors hover:text-white">
            Conditions d&apos;utilisation
          </a>
          <span className="text-white/25">|</span>
          <a href="/confidentiality" className="text-white/60 transition-colors hover:text-white">
            Politique de confidentialité
          </a>
        </div>
      </div>
    </footer>
  );
}

export default function Confidentiality() {
  return (
    <>
      <PageMeta
        title="Politique de confidentialité"
        description="Consultez la politique de confidentialité de LMS Technologies."
        path="/confidentiality"
      />
      <div style={{ fontFamily: "'Inter', sans-serif" }}>
        <LegalNavbar />
        <main className="min-h-screen bg-white px-6 py-24 md:px-12 md:pt-28">
          <section className="mx-auto max-w-4xl">
            <h1 className="text-4xl font-semibold text-[#0C1925] md:text-5xl">Politique de confidentialité</h1>
            <p className="mt-3 text-sm text-[#1a1a2e]/60 md:text-base">
              Conforme à la Loi 25 (Québec) — Dernière mise à jour : 6 mai 2026
            </p>

          <div className="mt-10 space-y-8 text-[#1a1a2e]/85">
            <section>
              <h2 className="text-2xl font-semibold text-[#0C1925]">1. Notre engagement</h2>
              <p className="mt-3 leading-relaxed">
                Chez LMS Technologies, la protection de vos informations personnelles est une priorité.
                Ce document décrit de façon transparente quelles données nous recueillons, pourquoi nous le
                faisons, et comment nous les protégeons lorsque vous utilisez notre site web ou que vous
                communiquez avec nous.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#0C1925]">2. Ce que nous collectons</h2>
              <p className="mt-3 leading-relaxed">
                Lorsque vous remplissez un formulaire sur notre site, vous pouvez être amené à nous fournir
                certaines informations :
              </p>
              <ul className="mt-3 list-disc space-y-2 pl-6 leading-relaxed">
                <li>Vos coordonnées : nom, prénom, adresse courriel et numéro de téléphone.</li>
                <li>
                  Votre contexte professionnel : nom de votre entreprise, secteur d&apos;activité et nature de
                  votre projet web.
                </li>
              </ul>
              <p className="mt-3 leading-relaxed">
                Nous ne collectons que ce qui est strictement nécessaire pour vous répondre adéquatement.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#0C1925]">3. Comment nous utilisons vos données</h2>
              <p className="mt-3 leading-relaxed">
                Les informations que vous nous transmettez servent uniquement à vous offrir un service
                personnalisé. Plus précisément, nous les utilisons pour analyser votre projet, préparer une
                soumission adaptée à vos besoins, et planifier une première rencontre avec vous. Vos données
                ne sont jamais vendues ni partagées à des fins commerciales.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#0C1925]">4. Où sont stockées vos données</h2>
              <p className="mt-3 leading-relaxed">
                Notre site est hébergé sur l&apos;infrastructure de Cloudflare, un fournisseur reconnu mondialement
                pour la sécurité de ses systèmes. Nous privilégions des solutions qui maintiennent vos données
                au Canada ou au Québec dans la mesure du possible, en conformité avec les exigences de la Loi 25.
                Pour les projets clients nécessitant une base de données, nous utilisons également DigitalOcean, qui possède des serveurs situés au Canada.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#0C1925]">5. Analytique et cookies</h2>
              <p className="mt-3 leading-relaxed">
                Nous utilisons Cloudflare Web Analytics pour mesurer l&apos;achalandage de notre site. Contrairement
                à Google Analytics, cet outil ne dépose aucun cookie sur votre appareil et ne recueille aucune
                donnée personnelle identifiable. Vous n&apos;avez donc pas à accepter quoi que ce soit pour naviguer
                sur notre site.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#0C1925]">6. Vos droits selon la Loi 25</h2>
              <p className="mt-3 leading-relaxed">
                La législation québécoise vous accorde plusieurs droits sur vos renseignements personnels. Vous
                pouvez en tout temps demander à consulter les données que nous détenons sur vous, les faire
                corriger si elles sont inexactes, ou retirer votre consentement. Notez que le retrait de votre
                consentement pourrait limiter notre capacité à vous offrir nos services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#0C1925]">7. Responsable de la confidentialité</h2>
              <p className="mt-3 leading-relaxed">
                Toute demande relative à vos données personnelles peut être acheminée directement à notre
                responsable désigné :
              </p>
              <p className="mt-3 leading-relaxed">
                LMS Technologies
                <br />
                409 Saint-Zéphirin, La Tuque
                <br />
                lmstechnologieweb@gmail.com
                <br />
                819-680-2042,
                873-664-7495
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#0C1925]">8. Mise à jour de cette politique</h2>
              <p className="mt-3 leading-relaxed">
                Cette politique peut être révisée à tout moment pour refléter l&apos;évolution de nos pratiques ou
                des exigences légales. La date de mise à jour en haut de page indique toujours la version la plus
                récente.
              </p>
            </section>
            </div>
          </section>
        </main>
        <LegalFooter />
      </div>
    </>
  );
}
